import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../services/api';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  roles?: string[];
}

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: '📍', roles: ['SUPERADMIN', 'ADMIN', 'EMPLOYEE', 'CLIENT'] },
  { path: '/repo', label: 'Panel Incaute', icon: '🚗', roles: ['REPO_AGENT'] },
  { path: '/devices', label: 'Dispositivos', icon: '📱' },
  { path: '/history', label: 'Historial', icon: '🗺️' },
  { path: '/geofences', label: 'Geocercas', icon: '⭕', roles: ['SUPERADMIN', 'ADMIN', 'EMPLOYEE'] },
  { path: '/alerts', label: 'Alertas', icon: '🔔' },
  { path: '/clients', label: 'Clientes', icon: '👤', roles: ['SUPERADMIN', 'ADMIN', 'EMPLOYEE'] },
  { path: '/reports', label: 'Reportes', icon: '📊', roles: ['SUPERADMIN', 'ADMIN', 'EMPLOYEE'] },
  { path: '/users', label: 'Usuarios', icon: '👥', roles: ['SUPERADMIN', 'ADMIN', 'EMPLOYEE'] },
  // Platform-only: tenant management and API integrations are SUPERADMIN exclusive.
  { path: '/companies', label: 'Empresas', icon: '🏢', roles: ['SUPERADMIN'] },
  { path: '/integrations', label: 'Integraciones', icon: '🔑', roles: ['SUPERADMIN'] },
];

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshToken, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Set a per-route document title so tabs/screen readers distinguish sections.
  useEffect(() => {
    const base = 'GyleLogistic';
    const match = navItems.find(
      (i) => location.pathname === i.path || location.pathname.startsWith(`${i.path}/`)
    );
    document.title = match ? `${match.label} — ${base}` : base;
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } finally {
      logout();
      navigate('/login');
    }
  };

  const closeSidebar = () => setSidebarOpen(false);

  const filteredNavItems = navItems.filter((item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role || '');
  });

  return (
    <div className="flex h-screen bg-cream-100">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar (static on desktop, slide-in drawer on mobile) */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-primary-900 text-white flex flex-col shadow-xl transition-transform duration-200 lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo Header */}
        <div className="p-4 border-b border-primary-800">
          <div className="flex items-center space-x-3">
            <img
              src="/logotrack.png"
              alt="GyleLogistic"
              className="h-12 w-auto"
            />
          </div>
          <p className="text-xs text-primary-300 mt-2">Sistema de Rastreo GPS</p>
        </div>

        <nav className="mt-2 flex-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center px-4 py-3 text-sm transition-all duration-200 ${
                  isActive
                    ? 'bg-secondary-700 text-white font-medium border-l-4 border-secondary-300'
                    : 'text-primary-100 hover:bg-primary-800 hover:text-white border-l-4 border-transparent'
                }`
              }
            >
              <span className="mr-3 text-lg" aria-hidden="true">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User Profile Section */}
        <div className="p-4 border-t border-primary-800 bg-primary-950">
          <div className="mb-3">
            <p className="text-sm font-semibold text-white">{user?.name}</p>
            <p className="text-xs text-primary-300">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-secondary-700 text-white rounded-full capitalize">
              {user?.role?.toLowerCase().replace('_', ' ')}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-sm text-white bg-primary-700 rounded-lg hover:bg-primary-600 transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Cerrar Sesión</span>
          </button>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-primary-800 bg-primary-950 text-center">
          <p className="text-xs text-primary-400">
            Desarrollado por{' '}
            <a
              href="https://www.theeruditionsoftware.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary-400 hover:text-secondary-300 transition-colors"
            >
              The Erudition Software
            </a>
          </p>
        </div>
      </aside>

      {/* Main content column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar with hamburger */}
        <header className="flex items-center gap-3 border-b border-primary-800 bg-primary-900 p-3 text-white lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menu de navegacion"
            aria-expanded={sidebarOpen}
            className="rounded-md p-1 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-secondary-400"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img src="/logotrack.png" alt="GyleLogistic" className="h-8 w-auto" />
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto bg-cream-100">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
